<template>
  <div id="app" :class="{ 'has-results': hasResults }">
    <notifications position="top right" classes="my-notification" />
    <router-view />
    <ScrollToTop />    
    <transition name="fade">
      <SurveyModal v-if="showSurveyModal" @close="closeSurveyModal" @completed="onSurveyCompleted" />
    </transition>
  </div>
</template>

<script>
import ScrollToTop from './components/ScrollToTop.vue';
import SurveyModal from './components/SurveyModal.vue';

export default {
  name: 'App',
  components: {
    ScrollToTop,
    SurveyModal
  },
  data() {
    return {
      showSurveyModal: false
    };
  },
  computed: {
    hasResults() {
      // Check if games exist in store
      return this.$store.state.games && this.$store.state.games.length > 0;
    }
  },
  mounted() {
    this.checkSurveyTrigger();
    // Listen for search complete events if we want to trigger after N searches
    // For now we can check on mount or provide a global event bus/store watcher
    this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'setGames' && state.games.length > 0) {
        this.incrementSearchCount();
      }
    });
  },
  methods: {
    incrementSearchCount() {
      const count = parseInt(localStorage.getItem('searchCount') || '0');
      const newCount = count + 1;
      localStorage.setItem('searchCount', newCount);

      // Trigger survey after 5 successful searches if logged in and not already completed
      const surveyCompleted = localStorage.getItem('surveyCompleted');
      const isLoggedIn = this.$store.getters.isLoggedIn;
      if (isLoggedIn && !surveyCompleted && newCount >= 5) {
        // Add a small delay so it doesn't pop up immediately with results
        setTimeout(() => {
          this.showSurveyModal = true;
        }, 2000);
      }
    },
    checkSurveyTrigger() {
      // Optional: Check on load if we should show it (e.g. they closed it without completing)
      // For now, only trigger on fresh searches to be less annoying
    },
    closeSurveyModal() {
      this.showSurveyModal = false;
      // Maybe reset count or set a "remind later" flag?
      // For now, let's bump the count requirement so it doesn't show immediately again
      const count = parseInt(localStorage.getItem('searchCount') || '0');
      localStorage.setItem('searchCount', count - 4); // Show again in 4 searches
    },
    onSurveyCompleted() {
      this.showSurveyModal = false;
      localStorage.setItem('surveyCompleted', 'true');
    }
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');

html, body {
  margin: 0;
  padding: 0;
  min-height: 100%;
  background-color: #09090b; /* Darker Charcoal */
  font-family: 'Space Grotesk', sans-serif;
  color: #f3f4f6;
  /* Removed overflow: hidden to allow global scrolling */
}

/* Ensure form elements inherit font family */
input, button, textarea, select {
  font-family: inherit;
}

#app {
  min-height: 100vh;
  width: 100%;
  position: relative;
  /* Default: Flat dark background (no gradient) */
  background: #0d0d0f; 
  transition: background 1s ease; /* Low key smooth transition */
}

#app.has-results {
  /* Premium radial gradient ONLY when games are present */
  background: radial-gradient(circle at 50% -20%, #101012 0%, #000000 100%);
}

.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 60px 20px;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
}

/* Global Scrollbar Styling */
::-webkit-scrollbar {
  width: 8px;
  background: #18181b;
}

::-webkit-scrollbar-thumb {
  background: #3f3f46;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #52525b;
}

.app-header {
  text-align: center;
  margin-bottom: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 12px;
}

.logo-icon {
  font-size: 48px;
  /* Removed float animation for a more stable look */
  text-shadow: 0 0 20px rgba(0, 220, 130, 0.5);
}

.app-title {
  font-size: 4rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: -1px;
  text-shadow: 2px 2px 0px #00dc82; /* Retro shadow effect */
}

.app-subtitle {
  color: #a1a1aa;
  font-size: 1.1rem;
  font-weight: 500;
  margin-top: 8px;
  letter-spacing: 0.5px;
}

.auth-section {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Search Mode Toggle */
.search-mode-toggle {
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
  gap: 12px;
}

.search-mode-toggle button {
  background: #18181b;
  border: 1px solid #27272a;
  color: #71717a;
  padding: 12px 24px;
  border-radius: 4px; /* Sharp corners */
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.search-mode-toggle button:hover:not(.active) {
  background: #27272a;
  color: #e4e4e7;
}

.search-mode-toggle button:focus {
  outline: 2px solid #00dc82;
  outline-offset: 2px;
}

.search-mode-toggle button.active {
  background: #00dc82;
  border-color: #00dc82;
  color: #000000;
}

.linked-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  background: rgba(0, 0, 0, 0.2);
  color: #064e3b; /* Dark green text on green bg */
  border-radius: 2px;
      border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Custom Notification Styles - Target the library classes specifically */
.vue-notification-group .vue-notification-wrapper .my-notification {
  margin: 10px;
  margin-bottom: 0;
  border-radius: 4px;
  font-size: 1rem;
  padding: 16px;
  color: #ffffff;
  background: #18181b;
  border-left: 4px solid #00dc82;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
}

.vue-notification-group .vue-notification-wrapper .my-notification .notification-title {
  font-weight: 700;
  font-size: 1rem;
  text-transform: uppercase;
  color: #00dc82;
}

.vue-notification-group .vue-notification-wrapper .my-notification .notification-content {
  font-size: 0.9rem;
  color: #e4e4e7;
}

.vue-notification-group .vue-notification-wrapper .my-notification.success {
  background: #062c19;
  border-left-color: #00dc82;
}

.vue-notification-group .vue-notification-wrapper .my-notification.error {
  background: #2b1212;
  border-left-color: #ef4444;
}

.vue-notification-group .vue-notification-wrapper .my-notification.error .notification-title {
  color: #ef4444;
}

.vue-notification-group .vue-notification-wrapper .my-notification.warn {
  background: #2b200b;
  border-left-color: #f59e0b;
}

.vue-notification-group .vue-notification-wrapper .my-notification.warn .notification-title {
  color: #f59e0b;
}

/* Steam Input Section */
.steam-input-section {
  margin-bottom: 32px;
  padding: 32px;
  background: #121215;
  border: 1px solid #27272a;
  border-radius: 8px;
  display: block;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.input-group label {
  font-weight: 700;
  color: #e4e4e7;
  text-transform: uppercase;
  font-size: 0.9rem;
  letter-spacing: 0.5px;
}

.input-group input {
  padding: 16px 20px;
  border-radius: 4px;
  border: 1px solid #3f3f46;
  background: #09090b;
  color: #ffffff;
  font-size: 1.1rem;
  transition: border-color 0.2s ease;
}

.input-group input:focus {
  outline: none;
  border-color: #00dc82;
}

.input-group input::placeholder {
  color: #52525b;
}

.helper-text {
  color: #71717a;
  font-size: 0.85rem;
  margin-top: -8px;
}

.submit-btn {
  background: #00dc82;
  border: none;
  color: #000000;
  padding: 14px 28px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  transition: all 0.2s ease;
  align-self: flex-start;
  letter-spacing: 0.5px;
}

.submit-btn:hover:not(:disabled) {
  background: #10b981;
  transform: translateY(-2px);
  box-shadow: 0 4px 0 #047857; /* 3D button effect */
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: none;
}

.submit-btn:focus {
  outline: 2px solid #00dc82;
  outline-offset: 4px;
}

.submit-btn:disabled {
  background: #27272a;
  color: #52525b;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Already Linked Message */
.already-linked-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 24px;
  background: #062c19; /* Dark Green bg */
  border: 1px solid #059669;
  border-left: 4px solid #059669;
  border-radius: 4px;
  animation: slideDown 0.3s ease-out;
}

.info-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
  color: #34d399;
}

.info-title {
  font-weight: 700;
  color: #34d399;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.info-description {
  color: #d1d5db;
  font-size: 0.95rem;
}

.loading-indicator {
  color: #00dc82;
  text-align: center;
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  text-transform: uppercase;
}

/* Steam Profile Section */
.steam-profile-section {
  background: #121215;
  border: 1px solid #27272a;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  animation: slideDown 0.3s ease-out;
}

.steam-profile-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.steam-avatar {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  object-fit: cover;
  border: 2px solid #00dc82;
}

.steam-profile-info h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #f3f4f6;
  font-weight: 700;
}

.steam-profile-info p {
  margin: 0;
  color: #a1a1aa;
  font-size: 0.9rem;
}

.linked-text {
  color: #00dc82;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
}

.clear-profile-btn {
  background: transparent;
  border: 1px solid #ef4444;
  color: #ef4444;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  transition: all 0.2s ease;
}

.clear-profile-btn:hover {
  background: #ef4444;
  color: white;
}

.clear-profile-btn:focus {
  outline: 2px solid #ef4444;
  outline-offset: 2px;
}

.results-section {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.results-header {
  margin-bottom: 32px;
  border-bottom: 2px solid #27272a; /* Divider line */
  padding-bottom: 16px;
}

.results-header h2 {
  font-size: 2rem;
  font-weight: 800;
  color: #f3f4f6;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: -0.5px;
}

.results-subtitle {
  color: #a1a1aa;
  font-size: 1rem;
}

.no-results {
  text-align: center;
  padding: 80px 20px;
  color: #71717a;
  border: 2px dashed #27272a;
  border-radius: 8px;
}



.no-results h3 {
  font-size: 1.5rem;
  color: #e4e4e7;
  margin-bottom: 8px;
  text-transform: uppercase;
  font-weight: 700;
}

.no-results p {
  font-size: 1.1rem;
  color: #71717a;
}

.loading-state {
  text-align: center;
  padding: 80px 20px;
}

.loading-state p {
  color: #00dc82;
  font-size: 1.2rem;
  margin-top: 24px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  margin: 0 auto;
  border: 6px solid #27272a;
  border-top: 6px solid #00dc82;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.55, 0.055, 0.675, 0.19) infinite;
  will-change: transform;
}

.loading-spinner.small {
  width: 24px;
  height: 24px;
  border-width: 3px;
  border-top-width: 3px;
  display: inline-block;
  margin: 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  background: #2b1212; /* Dark red bg */
  border: 1px solid #ef4444;
  border-left: 4px solid #ef4444;
  color: #fca5a5;
  padding: 24px;
  border-radius: 4px;
  text-align: center;
  margin: 20px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  animation: slideDown 0.3s ease-out;
}

.error-icon {
  font-size: 2rem;
}

.steam-error {
  margin-top: 12px;
  margin-bottom: 0;
}

.loading-more {
  text-align: center;
  padding: 40px 20px;
  color: #71717a;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-transform: uppercase;
  font-weight: 600;
}

.scroll-trigger {
  height: 20px;
}

/* Accessibility - Reduce motion */
@media (prefers-reduced-motion: reduce) {
  .logo-icon,
  .app-background::before,
  .loading-spinner {
    animation: none;
  }

  .results-section,
  .steam-profile-section,
  .error-message,
  .already-linked-message {
    animation: none;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 20px 16px; /* Reduced side padding */
    margin: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .app-title {
    font-size: 2.5rem;
  }

  .app-subtitle {
    font-size: 0.95rem;
  }

  .logo-icon {
    font-size: 40px;
  }

  .auth-section {
    margin-top: 20px;
  }

  .search-mode-toggle {
    flex-direction: column;
    align-items: stretch;
  }

  .search-mode-toggle button {
    width: 100%;
    justify-content: center;
  }

  .steam-input-section {
    padding: 20px;
  }

  .steam-profile-section {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .steam-profile-header {
    flex-direction: column;
    justify-content: center;
  }

  .results-header h2 {
    font-size: 1.75rem;
  }

  .no-results h3 {
    font-size: 1.25rem;
  }
}
</style>